import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trash2, Ruler, Loader2, ChevronDown } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { toast } from "sonner";
import { obterMedidasCliente, deletarMedidaCliente, MedidaConserto } from "@/utils/medidas-sincronizacao";

interface MedidasClienteCardProps {
  clienteId: string;
  onRefresh?: () => void;
}

function formatarTipoMedida(tipo: string): string {
  const mapa: Record<string, string> = {
    altura: "Altura",
    largura: "Largura",
    profundidade: "Profundidade",
    cintura: "Cintura",
    busto: "Busto",
    manga: "Manga",
    comprimento: "Comprimento",
    personalizada: "Personalizada",
    cava_manga: "Cava da Manga",
    comprimento_manga: "Comprimento da Manga",
    largura_costas: "Largura das Costas",
    ombro: "Ombro",
    quadril: "Quadril",
    coxa: "Coxa",
    tornozelo: "Tornozelo",
    comprimento_calca: "Comprimento da Calça",
    grossura_braco: "Grossura do Braço",
    cana_braco: "Cana do Braço",
    alca: "Alça",
    pescoco: "Pescoço",
  };
  return mapa[tipo] || tipo.replace(/_/g, " ");
}

export function MedidasClienteCard({ clienteId, onRefresh }: MedidasClienteCardProps) {
  const [medidas, setMedidas] = useState<MedidaConserto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeletando, setIsDeletando] = useState<string | null>(null);
  const [isRegistradasOpen, setIsRegistradasOpen] = useState(false);

  useEffect(() => {
    carregarMedidas();
  }, [clienteId]);

  const carregarMedidas = async () => {
    try {
      setIsLoading(true);
      const dados = await obterMedidasCliente(clienteId);
      setMedidas(dados);
    } catch (error) {
      console.error("Erro ao carregar medidas:", error);
      toast.error("Erro ao carregar medidas");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletarMedida = async (medidaId: string) => {
    try {
      setIsDeletando(medidaId);
      const result = await deletarMedidaCliente(clienteId, medidaId);
      if (result.ok) {
        toast.success("Medida deletada com sucesso");
        await carregarMedidas();
        onRefresh?.();
      } else {
        toast.error(result.error || "Erro ao deletar medida");
      }
    } catch (error) {
      console.error("Erro ao deletar medida:", error);
      toast.error("Erro ao deletar medida");
    } finally {
      setIsDeletando(null);
    }
  };

  const medidasConsertos = medidas.filter(m => m.pedido_numero && m.pedido_numero.trim() !== '');
  const medidasRegistradas = medidas.filter(m => !m.pedido_numero || m.pedido_numero.trim() === '');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Ruler className="w-5 h-5" />
          Histórico de Medidas
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
          </div>
        ) : medidas.length === 0 ? (
          <div className="py-8 text-center">
            <Ruler className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 font-semibold">Nenhuma medida registrada</p>
            <p className="text-sm text-gray-400">As medidas dos consertos e registros aparecerão aqui</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* SEÇÃO 1: Medidas de Consertos */}
            {medidasConsertos.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-purple-900 px-1">📋 Medidas de Consertos</h3>
                <div className="space-y-2">
                  {medidasConsertos
                    .sort(
                      (a, b) =>
                        new Date(b.data_criacao).getTime() -
                        new Date(a.data_criacao).getTime()
                    )
                    .map((medida) => (
                      <div
                        key={medida.id}
                        className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200 hover:bg-purple-100 transition"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-purple-900">
                              {formatarTipoMedida(medida.tipo_medida)}
                            </span>
                            <span className="text-lg font-bold text-purple-600">
                              {medida.valor}{medida.unidade === "cm" ? "cm" : '"'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-purple-700">
                            <span>Pedido: #{medida.pedido_numero}</span>
                            <span>•</span>
                            <span>{new Date(medida.data_criacao).toLocaleDateString("pt-BR")}</span>
                            {medida.notas && (
                              <>
                                <span>•</span>
                                <span className="italic">{medida.notas}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeletarMedida(medida.id)}
                          disabled={isDeletando === medida.id}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          {isDeletando === medida.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* SEÇÃO 2: Medidas Registradas */}
            {medidasRegistradas.length > 0 && (
              <Collapsible open={isRegistradasOpen} onOpenChange={setIsRegistradasOpen} className="space-y-2">
                <CollapsibleTrigger asChild>
                  <button className="flex items-center gap-2 text-sm font-semibold text-blue-900 px-1 py-2 hover:text-blue-700 cursor-pointer w-full">
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${isRegistradasOpen ? 'rotate-180' : ''}`}
                    />
                    📏 Medidas Registradas ({medidasRegistradas.length})
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-3">
                  {/* Agrupar por tipo de peça */}
                  {Object.entries(
                    medidasRegistradas.reduce((acc: Record<string, any[]>, medida: any) => {
                      const tipo = medida.tipo_peca || 'Sem tipo';
                      if (!acc[tipo]) acc[tipo] = [];
                      acc[tipo].push(medida);
                      return acc;
                    }, {})
                  )
                    .sort(([tipoA], [tipoB]) => tipoA.localeCompare(tipoB))
                    .map(([tipoPeca, medidasDoTipo]: [string, any[]]) => (
                      <div key={tipoPeca} className="border-l-4 border-l-purple-400 pl-4 space-y-2">
                        <h4 className="text-sm font-bold text-purple-900 uppercase tracking-wide">{tipoPeca}</h4>
                        {medidasDoTipo
                          .sort(
                            (a, b) =>
                              new Date(b.data_criacao).getTime() -
                              new Date(a.data_criacao).getTime()
                          )
                          .map((medida) => (
                      <div
                        key={medida.id}
                        className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg border border-purple-200 hover:from-purple-100 hover:to-violet-100 transition"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2">
                            <span className="text-base font-bold text-purple-900 flex-shrink-0">
                              {formatarTipoMedida(medida.tipo_medida)}
                            </span>
                            <span className="text-lg font-bold text-purple-600">
                              {medida.valor}
                            </span>
                            <span className="text-xs font-semibold text-purple-500 flex-shrink-0">
                              {medida.unidade === "cm" ? "cm" : '"'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-purple-600 mt-2">
                            <span>{new Date(medida.data_criacao).toLocaleDateString("pt-BR")}</span>
                            {medida.notas && !medida.notas.includes('[') && (
                              <>
                                <span>•</span>
                                <span className="italic text-purple-500">{medida.notas}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeletarMedida(medida.id)}
                          disabled={isDeletando === medida.id}
                          className="text-purple-600 hover:text-red-700 hover:bg-red-50 ml-2 flex-shrink-0"
                        >
                          {isDeletando === medida.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                          ))}
                      </div>
                    ))}
                </CollapsibleContent>
              </Collapsible>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
